import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  formatDate,
  getCurrentDate,
  formatDateForInput,
  parseDateString,
} from "../utils/dateUtils";
import { createNotification } from "../utils/notifications";

function Reports() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterInstructor, setFilterInstructor] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const itemsPerPage = 30;

  // New report creation state
  const [step, setStep] = useState(1); // 1 = input, 2 = overview
  const [selectedInstructorId, setSelectedInstructorId] = useState("");
  const [candidateHours, setCandidateHours] = useState({}); // { candidateId: hours }
  const [testResults, setTestResults] = useState([]); // [{ candidateId, testType: 'teorik'|'praktik', passed: true/false }]
  const [notes, setNotes] = useState("");
  const [reportDate, setReportDate] = useState(getCurrentDate());
  const [userRole, setUserRole] = useState(null);
  const [userEntityId, setUserEntityId] = useState(null);
  const [candidateSearchQuery, setCandidateSearchQuery] = useState("");

  // Fetch user role and entityId
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || null);
            setUserEntityId(userData.entityId || null);
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      } else {
        setUserRole(null);
        setUserEntityId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Auto-select instructor for instructors
  useEffect(() => {
    if (userRole === "instructor" && userEntityId && instructors.length > 0) {
      setSelectedInstructorId(userEntityId);
    }
  }, [userRole, userEntityId, instructors]);

  // Open add-report modal when instructor lands from login (first page)
  useEffect(() => {
    if (location.state?.openAddReport) {
      setIsModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openAddReport, location.pathname, navigate]);

  // Fetch all data
  const fetchInstructors = async () => {
    try {
      const instructorsSnapshot = await getDocs(collection(db, "instructors"));
      const instructorsData = instructorsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      instructorsData.sort((a, b) => {
        const nameA = (a.firstName || "").toLowerCase();
        const nameB = (b.firstName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setInstructors(instructorsData);
    } catch (error) {
      console.error("Error fetching instructors:", error);
      showToast(t("reports.toast.fetchInstructorsError"), "error");
      setInstructors([]);
    }
  };

  const fetchCandidates = async () => {
    try {
      const candidatesSnapshot = await getDocs(collection(db, "candidates"));
      const candidatesData = candidatesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setCandidates([]);
    }
  };

  const fetchReports = async () => {
    try {
      const reportsSnapshot = await getDocs(collection(db, "reports"));
      const reportsData = reportsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      reportsData.sort((a, b) => {
        const parseDate = (dateStr, fallbackTimestamp) => {
          if (!dateStr) {
            return fallbackTimestamp?.toDate
              ? fallbackTimestamp.toDate()
              : new Date(fallbackTimestamp || 0);
          }

          if (dateStr.includes("/")) {
            const parts = dateStr.split("/");
            if (parts.length === 3) {
              return new Date(
                parseInt(parts[2]),
                parseInt(parts[1]) - 1,
                parseInt(parts[0])
              );
            }
          }

          const parsed = new Date(dateStr);
          return isNaN(parsed.getTime())
            ? fallbackTimestamp?.toDate
              ? fallbackTimestamp.toDate()
              : new Date(fallbackTimestamp || 0)
            : parsed;
        };

        const dateA = parseDate(a.date, a.createdAt);
        const dateB = parseDate(b.date, b.createdAt);

        if (dateB.getTime() !== dateA.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }

        const createdAtA = a.createdAt?.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt || 0);
        const createdAtB = b.createdAt?.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt || 0);
        return createdAtB.getTime() - createdAtA.getTime();
      });

      setReports(reportsData);
    } catch (error) {
      console.error("Error fetching reports:", error);
      showToast(t("reports.toast.fetchError"), "error");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchInstructors(),
          fetchCandidates(),
          fetchReports(),
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate remaining hours for candidates
  const getCandidateRemainingHours = (candidate) => {
    const totalHours = parseFloat(candidate.totalHours) || 0;
    const bonusHours = parseFloat(candidate.bonusHours) || 0;
    const completedHours = parseFloat(candidate.completedHours) || 0;
    const completedBonusHours = parseFloat(candidate.completedBonusHours) || 0;

    return {
      normal: Math.max(0, totalHours - completedHours),
      bonus: Math.max(0, bonusHours - completedBonusHours),
      total: Math.max(
        0,
        totalHours + bonusHours - (completedHours + completedBonusHours)
      ),
    };
  };

  // Get candidates for selected instructor with remaining hours
  const instructorCandidates = useMemo(() => {
    if (!selectedInstructorId) return [];

    let filteredCandidates = candidates;

    // For instructors, only show their own candidates
    if (userRole === "instructor" && userEntityId) {
      filteredCandidates = candidates.filter((candidate) => {
        return candidate.assignedInstructorId === userEntityId;
      });
    }

    const searchLower = candidateSearchQuery.toLowerCase().trim();

    return filteredCandidates
      .filter((candidate) => {
        const matchesById =
          candidate.assignedInstructorId === selectedInstructorId;
        const instructor = instructors.find(
          (i) => i.id === selectedInstructorId
        );
        const matchesByName =
          instructor &&
          candidate.assignedInstructor ===
            `${instructor.firstName} ${instructor.lastName}`;
        // Include active and "Në Proces" (onProcess) candidates in add report
        const showInReport =
          candidate.status === "active" || candidate.status === "onProcess";

        if (!(matchesById || matchesByName) || !showInReport) {
          return false;
        }

        // Apply search filter
        if (searchLower) {
          const fullName = `${candidate.firstName || ""} ${candidate.lastName || ""}`.toLowerCase();
          const candidateId = (candidate.id || "").toLowerCase();
          const phoneNumber = (candidate.phoneNumber || "").toLowerCase();
          const phonePrefix = (candidate.phonePrefix || "").toLowerCase();
          const fullPhone = `${phonePrefix}${phoneNumber}`.toLowerCase();
          
          return (
            fullName.includes(searchLower) ||
            candidateId.includes(searchLower) ||
            phoneNumber.includes(searchLower) ||
            phonePrefix.includes(searchLower) ||
            fullPhone.includes(searchLower)
          );
        }

        return true;
      })
      .map((candidate) => {
        const remaining = getCandidateRemainingHours(candidate);
        return {
          ...candidate,
          remainingHours: remaining,
        };
      })
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [candidates, instructors, selectedInstructorId, userRole, userEntityId, candidateSearchQuery]);

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Get instructor name by ID
  const getInstructorName = (instructorId) => {
    if (!instructorId) return null;
    const instructor = instructors.find((i) => i.id === instructorId);
    return instructor ? `${instructor.firstName} ${instructor.lastName}` : null;
  };

  const REPORT_CREATED_BY_ADMIN_EMAIL = "burimiautoshkolla@gmail.com";
  const getReportCreatedByLabel = (report) => {
    const email = (report.createdByEmail || "").toLowerCase();
    if (email === REPORT_CREATED_BY_ADMIN_EMAIL.toLowerCase()) {
      return t("reports.createdBy.admin");
    }
    return t("reports.createdBy.instructor");
  };

  // Handle print report
  const handlePrintReport = () => {
    if (!viewingReport) return;

    const printWindow = window.open('', '_blank');
    const instructor = instructors.find((i) => i.id === viewingReport.instructorId);
    const instructorName = viewingReport.instructorName ||
      getInstructorName(viewingReport.instructorId) ||
      "-";
    const instructorPhone = instructor 
      ? (instructor.phonePrefix || '') + (instructor.phoneNumber || '')
      : "-";
    const reportDate = formatDate(viewingReport.date);
    
    
    const testTypeLabel = (testType) => {
      return testType === 'praktik' 
        ? t("candidates.form.practicalTestStatus") || "Testi Praktik"
        : t("candidates.form.theoryTestStatus") || "Testi Teorik";
    };

    const hoursHtml = viewingReport.candidateHours?.length > 0
      ? `
        <div style="margin-top: 35px;">
          <h3 style="font-size: 16px; font-weight: 700; margin-bottom: 14px; color: #000000; letter-spacing: 0.5px;">
            ${t("reports.form.candidateHours") || "Orët e Kandidatëve"}:
          </h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr>
                <th style="padding: 14px 16px; text-align: left; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">
                  ${t("reports.print.candidate") || "Kandidati"}
                </th>
                <th style="padding: 14px 16px; text-align: right; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">
                  ${t("reports.print.hours") || "Orët"}
                </th>
                <th style="padding: 14px 16px; text-align: center; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">
                  ${t("reports.print.type") || "Lloji"}
                </th>
              </tr>
            </thead>
            <tbody>
              ${viewingReport.candidateHours.map((ch, idx) => {
                const candidate = candidates.find((c) => c.id === ch.candidateId);
                const candidateIdDisplay = candidate?.candidateId || ch.candidateId || "";
                const hoursTypeLabel = ch.hoursType === "bonus" 
                  ? t("lessons.form.bonus") || "Bonus"
                  : ch.hoursType === "normalAndBonus"
                  ? `${t("lessons.form.normal") || "Normale"} + ${t("lessons.form.bonus") || "Bonus"}`
                  : t("lessons.form.normal") || "Normale";
                return `
                <tr style="${idx % 2 === 0 ? 'background-color: #ffffff;' : 'background-color: #f5f5f5;'}">
                  <td style="padding: 12px 16px; border-bottom: 1px solid #000000; font-size: 14px; color: #000000;">${ch.candidateName}${candidateIdDisplay ? ` (${candidateIdDisplay})` : ''}</td>
                  <td style="padding: 12px 16px; border-bottom: 1px solid #000000; text-align: right; font-weight: 600; font-size: 14px; color: #000000;">${Math.round(ch.hours)}h</td>
                  <td style="padding: 12px 16px; border-bottom: 1px solid #000000; text-align: center; font-size: 13px; color: #000000;">${hoursTypeLabel}</td>
                </tr>
              `;
              }).join('')}
              <tr style="background-color: #f5f5f5;">
                <td style="padding: 14px 16px; border-top: 2px solid #000000; font-weight: 700; font-size: 14px; color: #000000;">
                  ${t("reports.form.total") || "Totali"}
                </td>
                <td style="padding: 14px 16px; border-top: 2px solid #000000; text-align: right; font-weight: 700; font-size: 14px; color: #000000;">
                  ${Math.round(viewingReport.candidateHours.reduce((sum, ch) => sum + (parseFloat(ch.hours) || 0), 0))}h
                </td>
                <td style="padding: 14px 16px; border-top: 2px solid #000000;"></td>
              </tr>
            </tbody>
          </table>
        </div>
      `
      : '';

    const testsHtml = viewingReport.testResults?.length > 0
      ? `
        <div style="margin-top: 35px;">
          <h3 style="font-size: 16px; font-weight: 700; margin-bottom: 14px; color: #000000; letter-spacing: 0.5px;">
            ${t("reports.form.testResultsSummary") || "Rezultatet e Testeve"}:
          </h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr>
                <th style="padding: 14px 16px; text-align: left; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">
                  ${t("reports.print.candidate") || "Kandidati"}
                </th>
                <th style="padding: 14px 16px; text-align: left; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">
                  ${t("reports.print.testType") || "Lloji i Testit"}
                </th>
                <th style="padding: 14px 16px; text-align: center; font-weight: 700; font-size: 13px; color: #000000; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #000000;">
                  ${t("reports.print.result") || "Rezultati"}
                </th>
              </tr>
            </thead>
            <tbody>
              ${viewingReport.testResults.map((test, idx) => {
                const candidate = candidates.find((c) => c.id === test.candidateId);
                const candidateIdDisplay = candidate?.candidateId || test.candidateId || "";
                return `
                <tr style="${idx % 2 === 0 ? 'background-color: #ffffff;' : 'background-color: #f5f5f5;'}">
                  <td style="padding: 12px 16px; border-bottom: 1px solid #000000; font-size: 14px; color: #000000;">${test.candidateName}${candidateIdDisplay ? ` (${candidateIdDisplay})` : ''}</td>
                  <td style="padding: 12px 16px; border-bottom: 1px solid #000000; font-size: 14px; color: #000000;">${testTypeLabel(test.testType || 'teorik')}</td>
                  <td style="padding: 12px 16px; border-bottom: 1px solid #000000; text-align: center; font-size: 13px;">
                    <span style="display: inline-block; padding: 6px 14px; border-radius: 4px; font-weight: 700; ${test.passed ? 'background-color: #000000; color: #ffffff;' : 'background-color: #000000; color: #ffffff;'}">
                      ${test.passed ? t("reports.form.passed") : t("reports.form.failed")}
                    </span>
                  </td>
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>
        </div>
      `
      : '';

    const notesHtml = viewingReport.notes
      ? `
        <div style="margin-top: 35px;">
          <h3 style="font-size: 16px; font-weight: 700; margin-bottom: 8px; color: #000000; letter-spacing: 0.5px;">
            ${t("reports.form.notes") || "Shënime"}:
          </h3>
          <div style="white-space: pre-wrap; font-size: 14px; line-height: 1.7; color: #000000; text-align: left; padding: 0; margin: 0;">
            ${viewingReport.notes}
          </div>
        </div>
      `
      : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Raport Ditor - ${reportDate}</title>
          <style>
            @media print {
              body { margin: 0; padding: 20px; }
              .no-print { display: none; }
              @page {
                margin: 1.5cm;
              }
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: 'Arial', 'Helvetica', sans-serif;
              padding: 30px;
              color: #000000;
              max-width: 210mm;
              margin: 0 auto;
              background: #ffffff;
            }
            .header-top {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 25px;
            }
            .week-label {
              font-size: 15px;
              font-weight: 700;
              color: #000000;
              padding-top: 8px;
            }
            .logo-container {
              height: 70px;
            }
            .logo-container img {
              height: 100%;
              width: auto;
              object-fit: contain;
              max-width: 200px;
            }
            .title-section {
              text-align: center;
              margin-bottom: 8px;
              padding: 0;
            }
            .title-section h1 {
              margin: 0;
              font-size: 32px;
              font-weight: 700;
              color: #000000;
              letter-spacing: 1px;
              text-transform: uppercase;
            }
            .date-section {
              text-align: center;
              margin-bottom: 25px;
            }
            .date-section .date {
              font-size: 17px;
              font-weight: 600;
              color: #000000;
            }
            .divider-line {
              border-bottom: 2px solid #000000;
              margin-bottom: 30px;
            }
            .instructor-info {
              margin-bottom: 30px;
              padding: 20px 20px 20px 25px;
              background-color: #f5f5f5;
              border-left: 4px solid #000000;
            }
            .instructor-info h3 {
              font-size: 14px;
              font-weight: 700;
              margin-bottom: 16px;
              color: #000000;
              text-transform: uppercase;
              letter-spacing: 0.8px;
            }
            .instructor-details {
              display: flex;
              justify-content: space-between;
              flex-wrap: wrap;
            }
            .instructor-info p {
              margin: 0;
              font-size: 15px;
              line-height: 1.8;
              color: #000000;
            }
            .instructor-info .label {
              font-weight: 700;
              display: inline-block;
              min-width: 120px;
              color: #000000;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="header-top">
            <div class="week-label">
              ${t("reports.print.week") || "Java"}: _____
            </div>
            <div class="logo-container">
              <img src="${window.location.origin}/logoBlack.png" alt="Logo" onerror="this.style.display='none'" />
            </div>
          </div>
          
          <div class="title-section">
            <h1>${t("reports.print.dailyReport") || "Raport Ditor"}</h1>
          </div>
          
          <div class="date-section">
            <div class="date">${reportDate}</div>
          </div>
          
          <div class="divider-line"></div>
          
          <div class="instructor-info">
            <h3>${t("reports.print.instructorInfo") || "Informacioni i Instruktorit"}:</h3>
            <div class="instructor-details">
              <p>
                <span class="label">${t("reports.print.name") || "Emri"}:</span>
                ${instructorName}
              </p>
              <p>
                <span class="label">${t("reports.print.phone") || "Numri i Tel"}:</span>
                ${instructorPhone}
              </p>
            </div>
          </div>
          
          ${hoursHtml}
          ${testsHtml}
          ${notesHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Handle hours change
  const handleHoursChange = (candidateId, delta) => {
    setCandidateHours((prev) => {
      const current = prev[candidateId] || 0;
      const newValue = Math.max(0, current + delta);
      return { ...prev, [candidateId]: newValue };
    });
  };

  // Handle test result toggle
  const toggleTestResult = (candidateId) => {
    setTestResults((prev) => {
      const existing = prev.find((t) => t.candidateId === candidateId);
      if (existing) {
        return prev.filter((t) => t.candidateId !== candidateId);
      }
      return [...prev, { candidateId, testType: 'teorik', passed: true }];
    });
  };

  // Handle test result pass/fail change
  const updateTestResult = (candidateId, passed) => {
    setTestResults((prev) => {
      const existing = prev.find((t) => t.candidateId === candidateId);
      if (existing) {
        return prev.map((t) =>
          t.candidateId === candidateId ? { ...t, passed } : t
        );
      }
      return [...prev, { candidateId, testType: 'teorik', passed }];
    });
  };

  // Handle test type change
  const updateTestType = (candidateId, testType) => {
    setTestResults((prev) => {
      const existing = prev.find((t) => t.candidateId === candidateId);
      if (existing) {
        return prev.map((t) =>
          t.candidateId === candidateId ? { ...t, testType } : t
        );
      }
      return [...prev, { candidateId, testType, passed: true }];
    });
  };

  // Handle proceed to overview
  const handleProceed = () => {
    if (!selectedInstructorId) {
      showToast(t("reports.errors.instructorRequired"), "error");
      return;
    }

    const hasHours = Object.values(candidateHours).some((h) => h > 0);
    const hasTests = testResults.length > 0;

    if (!hasHours && !hasTests && !notes.trim()) {
      showToast(t("reports.errors.noData"), "error");
      return;
    }

    setStep(2);
  };

  // Handle go back
  const handleGoBack = () => {
    setStep(1);
  };

  // Handle confirm and save
  const handleConfirm = async () => {
    try {
      const instructor = instructors.find((i) => i.id === selectedInstructorId);
      if (!instructor) {
        showToast(t("reports.errors.instructorNotFound"), "error");
        return;
      }

      // Prepare report data
      const reportData = {
        instructorId: selectedInstructorId,
        instructorName: `${instructor.firstName} ${instructor.lastName}`,
        date: reportDate,
        candidateHours: Object.entries(candidateHours)
          .filter(([_, hours]) => hours > 0)
          .map(([candidateId, hours]) => {
            const candidate = candidates.find((c) => c.id === candidateId);
            const hoursValue = parseFloat(hours) || 0;
            let hoursType = "normal";
            
            if (candidate) {
              const remaining = getCandidateRemainingHours(candidate);
              const hoursToAdd = Math.min(hoursValue, remaining.normal);
              const bonusHoursToAdd = Math.max(0, hoursValue - remaining.normal);
              
              if (bonusHoursToAdd > 0 && hoursToAdd === 0) {
                hoursType = "bonus";
              } else if (bonusHoursToAdd > 0 && hoursToAdd > 0) {
                hoursType = "normalAndBonus";
              }
            }
            
            return {
              candidateId,
              candidateName: candidate
                ? `${candidate.firstName} ${candidate.lastName}`
                : "",
              hours: hoursValue,
              hoursType: hoursType,
            };
          }),
        testResults: testResults.map((test) => {
          const candidate = candidates.find((c) => c.id === test.candidateId);
          return {
            candidateId: test.candidateId,
            candidateName: candidate
              ? `${candidate.firstName} ${candidate.lastName}`
              : "",
            testType: test.testType || 'teorik',
            passed: test.passed,
          };
        }),
        notes: notes.trim(),
        createdAt: serverTimestamp(),
        createdByEmail: auth.currentUser?.email || "",
      };

      // Save report
      const docRef = await addDoc(collection(db, "reports"), reportData);

      // Show success and close modal immediately after successful save
      showToast(t("reports.toast.reportAdded"), "success");

      // Reset form
      setStep(1);
      setSelectedInstructorId("");
      setCandidateHours({});
      setTestResults([]);
      setNotes("");
      setReportDate(getCurrentDate());
      setIsModalOpen(false);

      // Handle non-critical operations separately
      try {
        // Update candidate completed hours
        for (const [candidateId, hoursValue] of Object.entries(
          candidateHours
        )) {
          const hours = parseFloat(hoursValue) || 0;
          if (hours > 0) {
            const candidate = candidates.find((c) => c.id === candidateId);
            if (candidate) {
              const currentCompleted =
                parseFloat(candidate.completedHours) || 0;
              const remaining = getCandidateRemainingHours(candidate);
              const hoursToAdd = Math.min(hours, remaining.normal);
              const bonusHoursToAdd = Math.max(0, hours - remaining.normal);

              const updateData = {};
              if (hoursToAdd > 0) {
                updateData.completedHours = currentCompleted + hoursToAdd;
              }
              if (bonusHoursToAdd > 0) {
                const currentCompletedBonus =
                  parseFloat(candidate.completedBonusHours) || 0;
                updateData.completedBonusHours =
                  currentCompletedBonus + bonusHoursToAdd;
              }

              if (Object.keys(updateData).length > 0) {
                await updateDoc(doc(db, "candidates", candidateId), updateData);
              }

              // Add lesson record
              await addDoc(collection(db, "lessons"), {
                candidateId: candidateId,
                candidateName: `${candidate.firstName} ${candidate.lastName}`,
                instructorId: selectedInstructorId,
                instructorName: reportData.instructorName,
                hours: parseFloat(hours),
                hoursType: hoursToAdd > 0 ? "normal" : "bonus",
                date: reportDate,
                timeStarted: null,
                timeEnded: null,
                notes: "",
                createdAt: serverTimestamp(),
              });
            }
          }
        }
      } catch (error) {
        console.error("Error updating candidates/lessons:", error);
        // Don't show error to user - report was saved successfully
      }

      // Create notification (non-critical)
      try {
        await createNotification(
          "report_added",
          "report",
          docRef.id,
          reportData.instructorName
        );
      } catch (error) {
        console.error("Error creating notification:", error);
        // Don't show error to user - report was saved successfully
      }

      // Refresh data (non-critical)
      try {
        await Promise.all([
          fetchCandidates(),
          fetchInstructors(),
          fetchReports(),
        ]);
      } catch (error) {
        console.error("Error refreshing data:", error);
        // Don't show error to user - report was saved successfully
      }
    } catch (error) {
      console.error("Error saving report:", error);
      showToast(t("reports.toast.saveError"), "error");
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    let todayCount = 0;
    let monthCount = 0;
    let yearCount = 0;
    let allTimeCount = reports.length;

    reports.forEach((report) => {
      const reportDate = report.date
        ? report.date.includes("/")
          ? (() => {
              const parts = report.date.split("/");
              if (parts.length === 3) {
                return new Date(
                  parseInt(parts[2]),
                  parseInt(parts[1]) - 1,
                  parseInt(parts[0])
                );
              }
              return new Date(report.date);
            })()
          : new Date(report.date)
        : report.createdAt?.toDate
        ? report.createdAt.toDate()
        : new Date(report.createdAt || 0);

      if (reportDate >= thisYear) {
        yearCount++;
        if (reportDate >= thisMonth) {
          monthCount++;
          if (reportDate >= today) {
            todayCount++;
          }
        }
      }
    });

    return {
      today: todayCount,
      month: monthCount,
      year: yearCount,
      allTime: allTimeCount,
    };
  }, [reports]);

  // Filter and search reports
  const filteredReports = useMemo(() => {
    let filtered = reports;

    // For instructors, only show their own reports
    if (userRole === "instructor" && userEntityId) {
      filtered = reports.filter((report) => {
        return report.instructorId === userEntityId;
      });
    }

    return filtered.filter((report) => {
      const matchesSearch =
        !searchQuery ||
        report.notes?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        getInstructorName(report.instructorId)
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesInstructor =
        !filterInstructor || report.instructorId === filterInstructor;

      let matchesDateRange = true;
      if (filterDateFrom || filterDateTo) {
        const reportDate = report.date
          ? report.date.includes("/")
            ? (() => {
                const parts = report.date.split("/");
                if (parts.length === 3) {
                  return new Date(
                    parseInt(parts[2]),
                    parseInt(parts[1]) - 1,
                    parseInt(parts[0])
                  );
                }
                return new Date(report.date);
              })()
            : new Date(report.date)
          : report.createdAt?.toDate
          ? report.createdAt.toDate()
          : new Date(report.createdAt || 0);

        if (filterDateFrom) {
          const fromDate = parseDateString(filterDateFrom);
          if (fromDate && reportDate < fromDate) {
            matchesDateRange = false;
          }
        }
        if (filterDateTo) {
          const toDate = parseDateString(filterDateTo);
          if (toDate) {
            const toDateEnd = new Date(toDate);
            toDateEnd.setHours(23, 59, 59, 999);
            if (reportDate > toDateEnd) {
              matchesDateRange = false;
            }
          }
        }
      }

      return matchesSearch && matchesInstructor && matchesDateRange;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reports,
    userRole,
    userEntityId,
    searchQuery,
    filterInstructor,
    filterDateFrom,
    filterDateTo,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = filteredReports.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterInstructor, filterDateFrom, filterDateTo]);

  // Handle view
  const handleView = (report) => {
    setViewingReport(report);
    setIsViewModalOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!reportToDelete) return;

    try {
      const reportName = reportToDelete.instructorName || "Unknown";
      await deleteDoc(doc(db, "reports", reportToDelete.id));
      await createNotification(
        "report_deleted",
        "report",
        reportToDelete.id,
        reportName
      );
      showToast(t("reports.toast.deleted"), "success");
      setIsDeleteModalOpen(false);
      setReportToDelete(null);
      await fetchReports();
    } catch (error) {
      console.error("Error deleting report:", error);
      showToast(t("reports.toast.deleteError"), "error");
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setStep(1);
    setSelectedInstructorId("");
    setCandidateHours({});
    setTestResults([]);
    setNotes("");
    setReportDate(getCurrentDate());
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast.show && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("reports.title")}
            </h2>
            <p className="text-gray-600">{t("reports.subtitle")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center space-x-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>{t("reports.addReport")}</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("reports.stats.today")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-4xl font-extrabold text-gray-900">
                {stats.today}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("reports.stats.reports")}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("reports.stats.thisMonth")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-4xl font-extrabold text-gray-900">
                {stats.month}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("reports.stats.reports")}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("reports.stats.thisYear")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-4xl font-extrabold text-gray-900">
                {stats.year}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("reports.stats.reports")}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("reports.stats.allTime")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-4xl font-extrabold text-gray-900">
                {stats.allTime}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("reports.stats.reports")}
              </span>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder={t("reports.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-10 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t("reports.filters.instructor")}:
                </label>
                <select
                  value={filterInstructor}
                  onChange={(e) => setFilterInstructor(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                >
                  <option value="">
                    {t("reports.filters.allInstructors")}
                  </option>
                  {instructors.map((instructor) => (
                    <option key={instructor.id} value={instructor.id}>
                      {instructor.firstName} {instructor.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t("reports.filters.dateFrom")}:
                </label>
                <input
                  type="date"
                  value={
                    filterDateFrom
                      ? formatDateForInput(
                          parseDateString(filterDateFrom) || filterDateFrom
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const date = new Date(value);
                      setFilterDateFrom(formatDate(date));
                    } else {
                      setFilterDateFrom("");
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t("reports.filters.dateTo")}:
                </label>
                <input
                  type="date"
                  value={
                    filterDateTo
                      ? formatDateForInput(
                          parseDateString(filterDateTo) || filterDateTo
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const date = new Date(value);
                      if (filterDateFrom) {
                        const fromDate = parseDateString(filterDateFrom);
                        if (fromDate && date < fromDate) {
                          return;
                        }
                      }
                      setFilterDateTo(formatDate(date));
                    } else {
                      setFilterDateTo("");
                    }
                  }}
                  min={
                    filterDateFrom
                      ? formatDateForInput(
                          parseDateString(filterDateFrom) || filterDateFrom
                        )
                      : undefined
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
                />
              </div>

              {(searchQuery ||
                filterInstructor ||
                filterDateFrom ||
                filterDateTo) && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setFilterInstructor("");
                    setFilterDateFrom("");
                    setFilterDateTo("");
                  }}
                  className="ml-auto px-3 py-2.5 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span>{t("reports.clearAll")}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Results Count */}
        {!loading && reports.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 px-2">
            <span>
              {t("reports.showing")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredReports.length > 0
                  ? `${startIndex + 1}-${Math.min(
                      endIndex,
                      filteredReports.length
                    )}`
                  : 0}
              </span>{" "}
              {t("reports.of")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredReports.length}
              </span>{" "}
              {t("reports.reportsText")}
              {filteredReports.length !== reports.length && (
                <span className="text-gray-500">
                  {" "}
                  ({t("reports.of")} {reports.length} {t("reports.total")})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Reports Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {filteredReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p>{t("reports.table.noReports")}</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 text-black hover:underline"
              >
                {t("reports.addFirstReport")}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("reports.table.createdBy")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("reports.table.title")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("reports.table.instructor")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("reports.table.description")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("reports.table.date")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("reports.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {report.createdByEmail != null && report.createdByEmail !== ""
                          ? getReportCreatedByLabel(report)
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {report.notes
                          ? report.notes.length > 50
                            ? `${report.notes.substring(0, 50)}...`
                            : report.notes
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {report.instructorName ||
                          getInstructorName(report.instructorId) ||
                          "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {report.candidateHours?.length > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2">
                            {Math.round(
                              report.candidateHours.reduce(
                                (sum, ch) => sum + (parseFloat(ch.hours) || 0),
                                0
                              )
                            )}{" "}
                            {t("reports.hours")}
                          </span>
                        )}
                        {report.testResults?.length > 0 && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {report.testResults.length} {t("reports.tests")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(report.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleView(report)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title={t("reports.actions.view")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setReportToDelete(report);
                              setIsDeleteModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title={t("reports.actions.delete")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredReports.length > itemsPerPage && (
          <div className="flex items-center justify-center space-x-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.previous")}
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-black text-white border-black"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>

      {/* Add Report Modal - Step 1: Input */}
      {isModalOpen && step === 1 && (
        <div className="fixed top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {t("reports.form.createReport")}
              </h3>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Instructor Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("reports.form.instructor")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedInstructorId}
                    onChange={(e) => {
                      setSelectedInstructorId(e.target.value);
                      setCandidateHours({});
                      setTestResults([]);
                      setCandidateSearchQuery("");
                    }}
                    disabled={userRole === "instructor"}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                      userRole === "instructor"
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <option value="">
                      {t("reports.form.selectInstructorPlaceholder")}
                    </option>
                    {instructors.map((instructor) => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.firstName} {instructor.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("reports.form.date")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                </div>

                {/* Candidates with Hours */}
                {selectedInstructorId && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("reports.form.candidateHours")}
                    </h4>
                    
                    {/* Search Bar */}
                    <div className="mb-4">
                      <input
                        type="text"
                        value={candidateSearchQuery}
                        onChange={(e) => setCandidateSearchQuery(e.target.value)}
                        placeholder={t("reports.form.searchCandidates") || "Search by name, ID, or phone number..."}
                        className="w-full px-4 py-3 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
                      />
                    </div>

                    {instructorCandidates.length > 0 ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="max-h-64 overflow-y-auto space-y-3 p-3">
                      {instructorCandidates.map((candidate) => {
                        const hours = candidateHours[candidate.id] || 0;
                        const remaining = candidate.remainingHours.total;
                        return (
                          <div
                            key={candidate.id}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {candidate.firstName} {candidate.lastName}
                              </p>
                              <p className="text-sm text-gray-500">
                                {t("reports.form.remainingHours")}:{" "}
                                {Math.round(remaining)}h |{" "}
                                {t("reports.form.addedHours")}:{" "}
                                {Math.round(hours)}h
                              </p>
                            </div>
                            <div className="flex items-center space-x-3">
                              <button
                                type="button"
                                onClick={() =>
                                  handleHoursChange(candidate.id, -1)
                                }
                                disabled={hours <= 0}
                                className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20 12H4"
                                  />
                                </svg>
                              </button>
                              <span className="w-16 text-center font-semibold text-gray-900">
                                {Math.round(hours)}h
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleHoursChange(candidate.id, 1)
                                }
                                className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        {candidateSearchQuery
                          ? t("reports.form.noCandidatesFound") || "No candidates found matching your search."
                          : t("reports.form.noActiveCandidates") || "No active candidates found for this instructor."}
                      </div>
                    )}
                  </div>
                )}

                {/* Test Results */}
                {selectedInstructorId && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("reports.form.testResults")}
                    </h4>
                    
                    {instructorCandidates.length > 0 ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="max-h-64 overflow-y-auto space-y-3 p-3">
                          {instructorCandidates.map((candidate) => {
                            const testResult = testResults.find(
                              (t) => t.candidateId === candidate.id
                            );
                            return (
                              <div
                                key={candidate.id}
                                className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg bg-white"
                              >
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-gray-900">
                                    {candidate.firstName} {candidate.lastName}
                                  </p>
                                  <input
                                    type="checkbox"
                                    checked={!!testResult}
                                    onChange={() => toggleTestResult(candidate.id)}
                                    className="w-5 h-5 text-black border-gray-300 rounded focus:ring-black cursor-pointer"
                                  />
                                </div>
                                {testResult && (
                                  <div className="flex flex-col gap-3">
                                    {/* Test Type Selector */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("reports.form.testType") || "Test Type"}
                                      </label>
                                      <select
                                        value={testResult.testType || 'teorik'}
                                        onChange={(e) => updateTestType(candidate.id, e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 text-sm"
                                      >
                                        <option value="teorik">
                                          {t("candidates.form.theoryTestStatus") || "Testi Teorik"}
                                        </option>
                                        <option value="praktik">
                                          {t("candidates.form.practicalTestStatus") || "Testi Praktik"}
                                        </option>
                                      </select>
                                    </div>
                                    {/* Pass/Fail Buttons */}
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateTestResult(candidate.id, true)
                                        }
                                        className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                                          testResult.passed
                                            ? "bg-green-500 text-white"
                                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                      >
                                        {t("reports.form.passed") || "Passed"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateTestResult(candidate.id, false)
                                        }
                                        className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                                          !testResult.passed
                                            ? "bg-red-500 text-white"
                                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                      >
                                        {t("reports.form.failed") || "Failed"}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        {candidateSearchQuery
                          ? t("reports.form.noCandidatesFound") || "No candidates found matching your search."
                          : t("reports.form.noActiveCandidates") || "No active candidates found for this instructor."}
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("reports.form.notes")}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder={t("reports.form.notesPlaceholder")}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleProceed}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                {t("reports.form.proceed")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Report Modal - Step 2: Overview */}
      {isModalOpen && step === 2 && (
        <div className="fixed top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {t("reports.form.reportOverview")}
              </h3>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Instructor and Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">
                      {t("reports.form.instructor")}
                    </p>
                    <p className="text-gray-900">
                      {getInstructorName(selectedInstructorId)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">
                      {t("reports.form.date")}
                    </p>
                    <p className="text-gray-900">{formatDate(reportDate)}</p>
                  </div>
                </div>

                {/* Hours Summary */}
                {Object.entries(candidateHours).some(
                  ([_, hours]) => hours > 0
                ) && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      {t("reports.form.hoursSummary")}
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(candidateHours)
                        .filter(([_, hours]) => hours > 0)
                        .map(([candidateId, hours]) => {
                          const candidate = candidates.find(
                            (c) => c.id === candidateId
                          );
                          return (
                            <div
                              key={candidateId}
                              className="flex justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <span className="text-gray-900">
                                {candidate
                                  ? `${candidate.firstName} ${candidate.lastName}`
                                  : candidateId}
                              </span>
                              <span className="font-semibold text-gray-900">
                                {Math.round(parseFloat(hours))}h
                              </span>
                            </div>
                          );
                        })}
                      <div className="flex justify-between p-3 border-t border-gray-200 font-semibold">
                        <span className="text-gray-900">
                          {t("reports.form.total")}
                        </span>
                        <span className="text-gray-900">
                          {Math.round(
                            Object.values(candidateHours).reduce(
                              (sum, h) => sum + parseFloat(h || 0),
                              0
                            )
                          )}
                          h
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Test Results Summary */}
                {testResults.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      {t("reports.form.testResultsSummary")}
                    </h4>
                    <div className="space-y-2">
                      {testResults.map((test) => {
                        const candidate = candidates.find(
                          (c) => c.id === test.candidateId
                        );
                        const testTypeLabel = test.testType === 'praktik' 
                          ? t("candidates.form.practicalTestStatus") || "Testi Praktik"
                          : t("candidates.form.theoryTestStatus") || "Testi Teorik";
                        return (
                          <div
                            key={test.candidateId}
                            className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex flex-col">
                              <span className="text-gray-900">
                                {candidate
                                  ? `${candidate.firstName} ${candidate.lastName}`
                                  : test.candidateId}
                              </span>
                              <span className="text-xs text-gray-500 mt-1">
                                {testTypeLabel}
                              </span>
                            </div>
                            <span
                              className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                test.passed
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {test.passed
                                ? t("reports.form.passed")
                                : t("reports.form.failed")}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {notes.trim() && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      {t("reports.form.notes")}
                    </h4>
                    <p className="text-gray-900 whitespace-pre-wrap p-3 bg-gray-50 rounded-lg">
                      {notes}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleGoBack}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("reports.form.goBack")}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                {t("reports.form.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {isViewModalOpen && viewingReport && (
        <div className="fixed top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {t("reports.view.title")}
              </h3>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t("reports.table.date")}
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDate(viewingReport.date)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t("reports.table.instructor")}
                    </label>
                    <p className="text-sm text-gray-900">
                      {viewingReport.instructorName ||
                        getInstructorName(viewingReport.instructorId) ||
                        "-"}
                    </p>
                  </div>
                </div>

                {viewingReport.candidateHours?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      {t("reports.form.hoursSummary")}
                    </label>
                    <div className="space-y-2">
                      {viewingReport.candidateHours.map((ch, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between p-2 bg-gray-50 rounded"
                        >
                          <span className="text-sm text-gray-900">
                            {ch.candidateName}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {Math.round(ch.hours)}h
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {viewingReport.testResults?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      {t("reports.form.testResultsSummary")}
                    </label>
                    <div className="space-y-2">
                      {viewingReport.testResults.map((test, idx) => {
                        const testTypeLabel = test.testType === 'praktik' 
                          ? t("candidates.form.practicalTestStatus") || "Testi Praktik"
                          : t("candidates.form.theoryTestStatus") || "Testi Teorik";
                        return (
                          <div
                            key={idx}
                            className="flex justify-between items-center p-2 bg-gray-50 rounded"
                          >
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-900">
                                {test.candidateName}
                              </span>
                              <span className="text-xs text-gray-500 mt-1">
                                {testTypeLabel}
                              </span>
                            </div>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                test.passed
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {test.passed
                                ? t("reports.form.passed")
                                : t("reports.form.failed")}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {viewingReport.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t("reports.form.notes")}
                    </label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {viewingReport.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
              <button
                onClick={handlePrintReport}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                  />
                </svg>
                <span>{t("reports.view.print") || "Print"}</span>
              </button>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && reportToDelete && (
        <div className="fixed top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {t("reports.delete.title")}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {t("reports.delete.message", {
                  instructor:
                    reportToDelete.instructorName ||
                    getInstructorName(reportToDelete.instructorId) ||
                    "",
                })}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setReportToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  {t("reports.delete.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Reports;
